import { twMerge } from "tailwind-merge";
import { NavigationBarMenu } from "../../../components/navigation/NavigationBar";
import { CustomTab, CustomTabContent } from "./lib/CustomTab";
import { useCustomTab } from "./lib/CustomTabContext";
import { ReactNode, useRef, useState } from "react";
import { Switch } from "@sellar-io/components";
import {
  Anchor,
  Button,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
  Icon,
  IconType,
  InputContainer,
  ListItemIconTile,
  SmallButton,
} from "@sellar-io/components";
import { InputLabel } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { Toast } from "../../../components/toast/Toast";
import { useToast } from "../../../components/toast/useToast.shared";
import { ProfilePicture, StockIndicator } from "../../../components";
import { useStorefrontLinkList } from "../../../hooks/api/v2/storefrontLink/useStorefrontLinkList";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import {
  SupplierProductGetResponseType,
  ProductListResponseType,
  StockListResponseType,
  StorefrontLinkListResponseType,
  SupplierRelationshipListNewResponseType,
} from "@sellar-io/api-v2/types";
import { useStorefrontLinkCreate } from "../../../hooks/api/v2/storefrontLink/useStorefrontLinkCreate";
import { usePriceListList } from "../../../hooks/api/v2/priceList/usePriceListList";
import { useSupplierRelationshipListNew } from "../../../hooks/api/v2/supplierRelationship/useSupplierRelationshipListNew";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { Alert } from "../../../components/alert/Alert";
import { useStorefrontLinkDelete } from "../../../hooks/api/v2/storefrontLink/useStorefrontLinkDelete";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useNavigate } from "react-router";
import { ORDER_BUYER } from "../../../constants/routes";
import { useStorefrontLinkUpdate } from "../../../hooks/api/v2/storefrontLink/useStorefrontLinkUpdate";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { useDebounceValue, useMediaQuery } from "usehooks-ts";
import { useProductList } from "../../../hooks/api/v2/product/useProductList";
import { useSupplierContainerTypesList } from "../../../hooks/api/v2/supplier/useSupplierContainerTypesList";
import { capitalize, formatCurrency } from "@sellar-io/common";
import {
  SkeletonContainer,
  SkeletonImage,
  SkeletonText,
} from "../../../components/skeleton/Skeleton";
import { useSupplierProduct } from "../../../hooks/api/v2/product/useSupplierProduct";
import { CategoryImage } from "../../stockList/categoryPage/categorySidePanel/CategoryImage";
import { ImageSlider } from "../../../components/imageSlider/ImageSlider";
import { useStockList } from "../../../hooks/api/v2/stockList/useStockList";
import { DietaryDisplayList } from "../../../components/dietaryDisplayList/DietaryDisplayList";
import { buildDietaryList } from "../../../components/dietaryDisplayList/buildDietaryList";
import dayjs from "dayjs";
import { BadgePurple } from "../../../components/badge/Badge";
import { OrderDiscountSummary } from "../../../components/discountSummary/OrderDiscountSummary";
import { useSupplierStorefrontV2 } from "../../../hooks/api/v2/supplier/useSupplierStorefrontV2";
import QRCode from "react-qr-code";
import { toPng } from "html-to-image";
import { downloadPNG } from "../../../utils/downloadHelpers";
import { buildDynamicKeyForStorefrontFilterStore } from "../../stockList/StorefrontFilterStore";
import { useStorefrontLinksV2Store } from "./StorefrontLinksV2Store";

enum StorefrontLinksV2Tabs {
  STOREFRONT,
  PRODUCTS_AND_FORMATS,
}

export const StorefrontLinksV2 = () => {
  return (
    <div className="flex min-h-full flex-1 flex-col">
      <NavigationBarMenu title="Share link" />
      <div className="flex flex-1 flex-col px-4 pt-4 md:px-8 md:pt-8">
        <CustomTab initialTab={StorefrontLinksV2Tabs.STOREFRONT}>
          <StorefrontLinksTabSelector />
          <CustomTabContent tab={StorefrontLinksV2Tabs.STOREFRONT}>
            <StorefrontLinksTab />
          </CustomTabContent>
          <CustomTabContent tab={StorefrontLinksV2Tabs.PRODUCTS_AND_FORMATS}>
            <StorefrontLinksProductAndFormatTab />
          </CustomTabContent>
        </CustomTab>
      </div>
    </div>
  );
};

const tabOptions = [
  { tab: StorefrontLinksV2Tabs.STOREFRONT, label: "Storefront" },
  {
    tab: StorefrontLinksV2Tabs.PRODUCTS_AND_FORMATS,
    label: "Products & formats",
  },
];

const StorefrontLinksTabSelector = () => {
  const { tab, setTab } = useCustomTab();

  return (
    <div className="flex">
      {tabOptions.map((tabOption) => {
        const isActive = tab === tabOption.tab;

        return (
          <button
            type="button"
            key={tabOption.tab}
            className={twMerge(
              "px-6 py-4 transition-colors",
              isActive
                ? "border-b-4 border-b-zinc-800 font-semibold text-black"
                : "cursor-pointer border-b-2 border-b-zinc-300 text-zinc-500",
            )}
            onClick={() => {
              setTab(tabOption.tab);
            }}
          >
            {tabOption.label}
          </button>
        );
      })}
      <div className="flex-1 border-b-2 border-b-zinc-300"></div>
    </div>
  );
};

type StorefrontLinksTabContainerProps = {
  title: string;
  description: ReactNode;
  children?: ReactNode;
  rightComponent?: ReactNode;
  disabled?: boolean;
};

const StorefrontLinksTabContainer = ({
  title,
  description,
  children,
  rightComponent,
  disabled,
}: StorefrontLinksTabContainerProps) => {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col justify-between gap-2 md:flex-row md:items-center">
        <div className="flex flex-col gap-1">
          <p
            className={twMerge(
              "font-semibold",
              disabled ? "text-zinc-700/30" : "text-zinc-800",
            )}
          >
            {title}
          </p>
          <div
            className={twMerge(disabled ? "text-zinc-700/30" : "text-zinc-700")}
          >
            {description}
          </div>
        </div>
        <div className="min-w-fit flex-1">
          {Boolean(rightComponent) && rightComponent}
        </div>
      </div>
      {children}
    </div>
  );
};

const StorefrontLinksTab = () => {
  const { business } = useSellarCurrentBusiness();
  const {
    isPending: isActivatingLink,
    mutationVariables,
    handleActivateCustomerStorefrontLink,
    handleActivateStorefrontDefaultLink,
    handleActivateStorefrontPriceTierLink,
  } = useActivateStorefrontLink();
  const isIncludePricingForStorefrontLinksEnabled =
    useRemoteFeatureFlags().isActive("enableIncludePricingForStorefrontLinks");
  const supplierUrlId = business?.Supplier?.urlId ?? "";
  const storefrontLinkListQuery = useStorefrontLinkList({});
  const priceTierListQuery = usePriceListList({
    query: [
      {
        type: "derivationTypeIncludes",
        value: ["custom", "flat", "percentage"],
        modifier: "IN",
      },
    ],
  });
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [debouncedCustomerSearchTerm] = useDebounceValue(
    customerSearchTerm,
    500,
  );
  const supplierRelationshipListNewQuery = useSupplierRelationshipListNew(
    {
      query: [
        {
          type: "name",
          modifier: "INCLUDES",
          value: debouncedCustomerSearchTerm,
        },
      ],
      limit: 5,
    },
    {
      enabled: Boolean(debouncedCustomerSearchTerm),
    },
  );

  const { selectedConnectionId, customer, setCustomer } =
    useStorefrontLinksV2Store();

  const storefrontLinks = storefrontLinkListQuery.data?.pages.flat() ?? [];
  const priceTiers = priceTierListQuery.data?.pages.flat() ?? [];
  const connections = supplierRelationshipListNewQuery.data?.flat() ?? [];

  const searchedCustomers: Customer[] = connections.map(
    convertConnectionToCustomer,
  );
  const customers: Customer[] = customer ? [customer] : searchedCustomers;

  const defaultStorefrontLink = storefrontLinks.find(
    (storefrontLink) =>
      !storefrontLink.PriceTier?.id &&
      !storefrontLink.SupplierRetailerConnectionId,
  );

  const canBuildStorefrontLink =
    defaultStorefrontLink?.password && supplierUrlId;

  const defaultStorefrontLinkAsString = canBuildStorefrontLink
    ? buildStorefrontLink({
        password: defaultStorefrontLink.password,
        supplierUrlId,
        formats: [],
        productId: null,
      })
    : "";

  const isDefaultStorefrontLinkActive = !!defaultStorefrontLink;

  const isCreatingDefaultStorefrontLink =
    isActivatingLink && mutationVariables?.type === "default";

  const isCreatingCustomerStorefrontLink =
    isActivatingLink && mutationVariables?.type === "connection";

  return (
    <div className="flex flex-col gap-6 py-4">
      <StorefrontLinksTabContainer
        title="General trade"
        description={
          <p>
            Share your main link with a broad audience (e.g. trade mailers,
            website and socials). For more information on how links work, visit
            our{" "}
            <Anchor
              className="inline-flex items-center gap-1 text-blue-600 underline"
              href="https://intercom.help/sellario/en/articles/10257582-supplier-basics-creating-links"
              target="_blank"
              rel="noopener noreferrer"
            >
              help centre.
              <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
            </Anchor>
          </p>
        }
      >
        <>
          {isDefaultStorefrontLinkActive &&
            isIncludePricingForStorefrontLinksEnabled && (
              <IncludePricingToggle storefrontLink={defaultStorefrontLink} />
            )}
          <StorefrontLinkItem
            storefrontLink={{
              id: defaultStorefrontLink?.id,
              name: "Main link",
              link: defaultStorefrontLinkAsString,
            }}
            supplierUrlId={supplierUrlId}
            activateProps={{
              isActivating: isCreatingDefaultStorefrontLink,
              onActivate: handleActivateStorefrontDefaultLink,
            }}
          />
        </>
      </StorefrontLinksTabContainer>

      <StorefrontLinksTabContainer
        title="Customers"
        description="Share a link with a specific customer. These links are unique to each customer so be sure to only share with the intended customer."
      >
        <StorefrontLinksTabCustomerSelector
          customers={customers}
          selectedCustomerId={selectedConnectionId}
          onSearchForCustomer={setCustomerSearchTerm}
          onSelectCustomer={(customerId) => {
            const selected =
              customers.find((c) => c.connectionId === customerId) ?? null;
            setCustomer(selected);
          }}
        />
        {selectedConnectionId && (
          <StorefrontLinkItemForConnection
            activateProps={{
              isActivating: isCreatingCustomerStorefrontLink,
              onActivate: () =>
                handleActivateCustomerStorefrontLink({
                  connectionId: selectedConnectionId,
                }),
            }}
            supplierUrlId={supplierUrlId}
            connectionId={selectedConnectionId}
            formats={[]}
            productId={null}
          />
        )}
      </StorefrontLinksTabContainer>

      <StorefrontLinksTabContainer
        title="Pricing"
        description="Use these links when you want to share specific pricing with a group of customers or prospects"
      >
        <div className="flex flex-col gap-2">
          {priceTiers.map((priceTier) => {
            const isCreatingPriceTierLink =
              isActivatingLink &&
              mutationVariables?.PriceTierId === priceTier.id &&
              mutationVariables.type === "priceTier";

            return (
              <StorefrontLinkItemForPriceTier
                key={priceTier.id}
                priceTier={priceTier}
                supplierUrlId={supplierUrlId}
                activateProps={{
                  isActivating: isCreatingPriceTierLink,
                  onActivate: () =>
                    handleActivateStorefrontPriceTierLink({
                      priceTierId: priceTier.id,
                    }),
                }}
                formats={[]}
                productId={null}
              />
            );
          })}
        </div>
      </StorefrontLinksTabContainer>
    </div>
  );
};

const StorefrontLinkItemPanel = ({
  rightComponent,
  name,
  link,
}: {
  rightComponent: ReactNode;
  name: string;
  link: string;
}) => {
  return (
    <div className="flex flex-col gap-3 rounded-lg bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="min-w-fit font-semibold text-zinc-800">{name}</span>
        <div>{Boolean(rightComponent) && rightComponent}</div>
      </div>
      <div className="flex items-center gap-2 rounded-sm border border-[#E4E4E7] bg-zinc-100 p-2 text-sm text-zinc-500">
        <div>
          <Icon icon="LINK" />
        </div>
        <span className="break-all">{link}</span>
      </div>
    </div>
  );
};

type StorefrontLinkItemProps = {
  supplierUrlId: string;
  storefrontLink: {
    id: number | undefined;
    name: string;
    link: string;
  };
  activateProps: {
    isActivating: boolean;
    onActivate: () => void;
  };
  disabled?: boolean;
};

const StorefrontLinkItem = (props: StorefrontLinkItemProps) => {
  const [openDeactivateLinkDialog, setOpenDeactivateLinkDialog] =
    useState(false);
  const [openViewQrCodeDialog, setOpenViewQrCodeDialog] = useState(false);

  const isMobile = useMediaQuery("(max-width: 768px)");
  const isDesktop = !isMobile;

  return (
    <>
      {isMobile && (
        <StorefrontLinkItemForMobile
          {...props}
          onOpenDeactivateDialog={() => setOpenDeactivateLinkDialog(true)}
          onOpenViewQrCodeDialog={() => setOpenViewQrCodeDialog(true)}
        />
      )}
      {isDesktop && (
        <StorefrontLinkItemForDesktop
          {...props}
          onOpenDeactivateDialog={() => setOpenDeactivateLinkDialog(true)}
          onOpenViewQrCodeDialog={() => setOpenViewQrCodeDialog(true)}
        />
      )}
      {props.storefrontLink.id && (
        <>
          <StorefrontLinkDeactivateDialog
            open={openDeactivateLinkDialog}
            storefrontLinkId={props.storefrontLink.id}
            onDismiss={() => setOpenDeactivateLinkDialog(false)}
          />
          <StorefrontLinkQRCodeDialog
            link={props.storefrontLink.link}
            linkName={props.storefrontLink.name}
            onDismiss={() => setOpenViewQrCodeDialog(false)}
            open={openViewQrCodeDialog}
          />
        </>
      )}
    </>
  );
};

const StorefrontLinkItemForMobile = ({
  supplierUrlId,
  storefrontLink,
  activateProps: { isActivating, onActivate },
  disabled,
  onOpenDeactivateDialog,
  onOpenViewQrCodeDialog,
}: StorefrontLinkItemProps & {
  onOpenDeactivateDialog: () => void;
  onOpenViewQrCodeDialog: () => void;
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!storefrontLink.id) {
    return (
      <StorefrontLinkItemPanel
        name={storefrontLink.name}
        link="Activate storefront link"
        rightComponent={
          <SmallButton
            disabled={isActivating || disabled}
            loading={isActivating}
            color="slim"
            onClick={onActivate}
          >
            <Icon icon="LIGHTNING_BOLT" />
          </SmallButton>
        }
      />
    );
  }

  return (
    <StorefrontLinkItemPanel
      name={storefrontLink.name}
      link={storefrontLink.link}
      rightComponent={
        <div className="flex gap-2">
          <SmallButton
            onClick={() => {
              toast({
                placement: "bottom",
                render: () => <Toast icon="LINK">Link copied!</Toast>,
              });
              navigator.clipboard.writeText(storefrontLink.link);
            }}
            color="slim"
            disabled={disabled}
          >
            <Icon icon="LINK" />
          </SmallButton>
          <SmallButton
            onClick={() => {
              navigate(ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlId }));
            }}
            color="slim"
            disabled={disabled}
          >
            <Icon icon="EYE" />
          </SmallButton>
          <Dropdown>
            <DropdownButton disabled={disabled}>
              <Button size="small" color="transparent" disabled={disabled}>
                <Icon icon="DOTS_VERTICAL" className="text-black" size={0.7} />
              </Button>
            </DropdownButton>
            <DropdownItems>
              <DropdownItem
                onClick={onOpenViewQrCodeDialog}
                disabled={disabled}
              >
                View QR
              </DropdownItem>
              <DropdownItem
                onClick={onOpenDeactivateDialog}
                className="text-red-500"
                disabled={disabled}
              >
                Deactivate
              </DropdownItem>
            </DropdownItems>
          </Dropdown>
        </div>
      }
    />
  );
};

const StorefrontLinkItemForDesktop = ({
  supplierUrlId,
  storefrontLink,
  activateProps: { isActivating, onActivate },
  disabled,
  onOpenDeactivateDialog,
  onOpenViewQrCodeDialog,
}: StorefrontLinkItemProps & {
  onOpenDeactivateDialog: () => void;
  onOpenViewQrCodeDialog: () => void;
}) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  if (!storefrontLink.id) {
    return (
      <StorefrontLinkItemPanel
        name={storefrontLink.name}
        link="Activate storefront link"
        rightComponent={
          <SmallButton
            disabled={isActivating || disabled}
            loading={isActivating}
            color="slim"
            onClick={onActivate}
          >
            <Icon icon="LIGHTNING_BOLT" />
            <span>Activate</span>
          </SmallButton>
        }
      />
    );
  }

  return (
    <StorefrontLinkItemPanel
      name={storefrontLink.name}
      link={storefrontLink.link}
      rightComponent={
        <div className="flex gap-2">
          <SmallButton
            onClick={() => {
              toast({
                placement: "bottom",
                render: () => <Toast icon="LINK">Link copied!</Toast>,
              });
              navigator.clipboard.writeText(storefrontLink.link);
            }}
            color="slim"
            disabled={disabled}
          >
            <Icon icon="LINK" />
            <span>Copy link</span>
          </SmallButton>
          <SmallButton
            onClick={onOpenViewQrCodeDialog}
            color="slim"
            disabled={disabled}
          >
            <Icon icon="QRCODE" />
            <span>QR</span>
          </SmallButton>

          <SmallButton
            onClick={() => {
              navigate(ORDER_BUYER.href({ supplierIdOrUrlId: supplierUrlId }));
            }}
            color="slim"
            disabled={disabled}
          >
            <Icon icon="EYE" />
            <span>Preview</span>
          </SmallButton>
          <Dropdown>
            <DropdownButton disabled={disabled}>
              <Button size="small" color="transparent" disabled={disabled}>
                <Icon icon="DOTS_VERTICAL" className="text-black" size={0.7} />
              </Button>
            </DropdownButton>
            <DropdownItems>
              <DropdownItem
                onClick={onOpenDeactivateDialog}
                className="text-red-500"
                disabled={disabled}
              >
                Deactivate
              </DropdownItem>
            </DropdownItems>
          </Dropdown>
        </div>
      }
    />
  );
};

const StorefrontLinkQRCodeDialog = ({
  link,
  linkName,
  open,
  onDismiss,
}: {
  link: string;
  linkName: string;
  open: boolean;
  onDismiss: () => void;
}) => {
  const qrCodeRef = useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();

  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadQRCode = () => {
    if (!qrCodeRef.current) {
      toast({
        placement: "bottom",
        render: () => (
          <Toast icon="ALERT">Unable to download this QR code</Toast>
        ),
      });
      return;
    }
    setIsDownloading(true);

    toPng(qrCodeRef.current, {
      cacheBust: false,
      backgroundColor: "white",
    })
      .then((dataUrl) => {
        downloadPNG("qr-code.png", dataUrl);
        onDismiss();
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <Dialog active={open} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="Download QR code"
      />
      <DialogContent className="flex justify-center">
        <div
          ref={qrCodeRef}
          className="flex flex-col gap-4 rounded-md border border-zinc-200 bg-zinc-100 p-4"
        >
          <QRCode
            size={256}
            className="h-56 w-56 rounded-lg bg-white p-4"
            value={link}
            viewBox={`0 0 256 256`}
          />
          <div className="flex items-center gap-2 rounded-lg bg-white p-2">
            <div className="rounded-lg bg-zinc-100 p-1">
              <Icon icon="ACCOUNT_MULTIPLE" size={0.7} />
            </div>
            <div>
              <p className="font-semibold">{linkName}</p>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className="flex justify-end">
        <Button
          disabled={isDownloading}
          loading={isDownloading}
          onClick={handleDownloadQRCode}
        >
          Download
        </Button>
      </DialogCTA>
    </Dialog>
  );
};

const StorefrontLinkItemForPriceTier = ({
  supplierUrlId,
  priceTier,
  activateProps: { isActivating, onActivate },
  formats,
  disabled,
  productId,
}: Pick<StorefrontLinkItemProps, "supplierUrlId" | "activateProps"> & {
  priceTier: {
    id: number;
    name: string;
  };
  disabled?: boolean;
  formats: string[];
  productId: number | null;
}) => {
  const storefrontLinkListQuery = useStorefrontLinkList(
    {
      query: [
        {
          type: "PriceTierId",
          modifier: "EQUALS",
          value: priceTier.id,
        },
      ],
    },
    {
      enabled: Boolean(priceTier.id),
    },
  );

  const priceTierStorefrontLink = storefrontLinkListQuery.data?.pages.flat()[0];
  const canBuildStorefrontLink =
    priceTierStorefrontLink?.password && supplierUrlId;

  const priceTierStorefrontLinkAsString = canBuildStorefrontLink
    ? buildStorefrontLink({
        password: priceTierStorefrontLink.password,
        supplierUrlId,
        formats,
        productId,
      })
    : "";

  return (
    <StorefrontLinkItem
      storefrontLink={{
        id: priceTierStorefrontLink?.id,
        name: priceTier.name,
        link: priceTierStorefrontLinkAsString,
      }}
      supplierUrlId={supplierUrlId}
      activateProps={{
        isActivating,
        onActivate,
      }}
      disabled={disabled}
    />
  );
};

const StorefrontLinkItemForConnection = ({
  supplierUrlId,
  connectionId,
  activateProps: { isActivating, onActivate },
  disabled,
  formats,
  productId,
}: Pick<StorefrontLinkItemProps, "supplierUrlId" | "activateProps"> & {
  connectionId: number;
  disabled?: boolean;
  formats: string[];
  productId: number | null;
}) => {
  const storefrontLinkListQuery = useStorefrontLinkList(
    {
      query: [
        {
          type: "ConnectionId",
          modifier: "EQUALS",
          value: connectionId,
        },
      ],
    },
    {
      enabled: Boolean(connectionId),
    },
  );

  const connectionStorefrontLink =
    storefrontLinkListQuery.data?.pages.flat()[0];

  const canBuildStorefrontLink =
    connectionStorefrontLink?.password && supplierUrlId;

  const connectionStorefrontLinkAsString = canBuildStorefrontLink
    ? buildStorefrontLink({
        password: connectionStorefrontLink.password,
        supplierUrlId,
        formats,
        productId,
      })
    : "";

  return (
    <StorefrontLinkItem
      storefrontLink={{
        id: connectionStorefrontLink?.id,
        name: "Your customer link",
        link: connectionStorefrontLinkAsString,
      }}
      supplierUrlId={supplierUrlId}
      activateProps={{
        isActivating,
        onActivate,
      }}
      disabled={disabled}
    />
  );
};

type Customer = {
  name: string;
  connectionId: number;
  logoUrl: string;
  postalCode: string;
  priceTierName: string;
  availabilityTierName: string;
};

type StorefrontLinksTabCustomerSelectorProps = {
  selectedCustomerId: number | null;
  customers: Customer[];
  onSearchForCustomer: (searchTerm: string) => void;
  onSelectCustomer: (customerId: number | null) => void;
};

const StorefrontLinksTabCustomerSelector = ({
  selectedCustomerId,
  customers,
  onSelectCustomer,
  onSearchForCustomer,
}: StorefrontLinksTabCustomerSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const selectedCustomer = customers.find(
    (customer) => customer.connectionId === selectedCustomerId,
  );

  if (selectedCustomer && selectedCustomerId) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex flex-col gap-1 text-sm">
          <span className="font-semibold">{selectedCustomer.name}</span>
          <span>Postcode: {selectedCustomer.postalCode}</span>
          <div className="flex flex-wrap gap-1">
            <CustomBadge
              label="Price tier"
              icon="TAG_OUTLINE"
              value={selectedCustomer.priceTierName}
            />
            <CustomBadge
              label="Product catalogue"
              icon="BOOK_OUTLINE"
              value={selectedCustomer.availabilityTierName}
            />
          </div>
        </div>
        <button
          type="button"
          className="hover:cursor-pointer"
          onClick={() => {
            onSelectCustomer(null);
          }}
        >
          <Icon icon="CLOSE" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <InputContainer>
        <InputText
          value={searchTerm}
          onChange={(event) => {
            const value = event.target.value;
            setSearchTerm(value);
            onSearchForCustomer(value);
          }}
          placeholder="Search for a customer"
        />
        <Icon icon="MAGNIFY" />
      </InputContainer>

      {customers.length > 0 && (
        <div className="flex flex-col rounded-lg border border-zinc-200 bg-white">
          {customers.map((customer) => (
            <button
              type="button"
              key={customer.connectionId}
              onClick={() => onSelectCustomer(customer.connectionId)}
              className="flex cursor-pointer items-center gap-4 px-4 py-2 text-start first-of-type:rounded-t-lg last-of-type:rounded-b-lg hover:bg-zinc-100"
            >
              <ProfilePicture logoUrl={customer.logoUrl} size="medium" />
              <div className="flex flex-col">
                <span className="font-semibold">{customer.name}</span>
                {Boolean(customer.priceTierName) && (
                  <span className="text-sm">
                    Assigned: {customer.priceTierName}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CustomBadge = ({
  value,
  label,
  className,
  icon,
}: {
  value: string;
  label?: string;
  className?: string;
  icon?: IconType;
}) => {
  return (
    <div
      className={twMerge(
        "flex items-center gap-1 rounded-lg border border-gray-300 bg-gray-100 px-2 py-1 text-sm",
        className,
      )}
    >
      {icon && <Icon icon={icon} />}
      {label && <span className="font-semibold">{label}:</span>}
      {value}
    </div>
  );
};

const buildStorefrontLink = ({
  password,
  supplierUrlId,
  formats,
  productId,
}: {
  password: string;
  supplierUrlId: string;
  formats: string[];
  productId: number | null;
}) => {
  const baseUrl = import.meta.env.VITE_URL;

  const state =
    formats.length > 0
      ? JSON.stringify({
          selectedTags: formats,
        })
      : "";

  const dynamicKeyForFilterStore =
    buildDynamicKeyForStorefrontFilterStore(supplierUrlId);

  const queryParameters = new URLSearchParams({
    ...(productId && { categoryId: `${productId}` }),
    ...(state && { [dynamicKeyForFilterStore]: state }),
    storefrontLink: password,
  });

  return `${baseUrl}/suppliers/${supplierUrlId}/order?${queryParameters}`;
};

type StorefrontLinkDeactivateDialogProps = {
  storefrontLinkId: number;
  open: boolean;
  onDismiss: () => void;
};

const StorefrontLinkDeactivateDialog = ({
  storefrontLinkId,
  open: openDialog,
  onDismiss,
}: StorefrontLinkDeactivateDialogProps) => {
  const [deactivateText, setDeactivateText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const handleDismiss = () => {
    onDismiss();
    setErrorMessage("");
    setDeactivateText("");
  };

  const isValidDeactivateString =
    Boolean(deactivateText) && deactivateText === "DEACTIVATE";

  const storefrontLinkDeleteMutation =
    useStorefrontLinkDelete(storefrontLinkId);

  const handleDeactivateStorefrontLink = () => {
    setErrorMessage("");

    if (!isValidDeactivateString) {
      setErrorMessage('Type the word "DEACTIVATE" to confirm');
      return;
    }
    storefrontLinkDeleteMutation.mutate(undefined, {
      onSuccess: () => {
        handleDismiss();
      },
      onError: (error) => {
        const message =
          apiV2AssertError(error)?.response?.data.message ??
          "We're unable to deactivate this link at this time. Try again later.";
        setErrorMessage(message);
      },
    });
  };

  const isDeactivatingLink = storefrontLinkDeleteMutation.isPending;

  return (
    <Dialog active={openDialog} onDismiss={handleDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={handleDismiss}
        title="Deactivate link"
      />
      <DialogContent>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleDeactivateStorefrontLink();
          }}
          className="flex flex-col gap-4"
        >
          {errorMessage && <Alert type="error" message={errorMessage} />}
          <div className="flex flex-col gap-2">
            <InputLabel>Type the word 'DEACTIVATE' to confirm</InputLabel>
            <InputContainer>
              <InputText
                value={deactivateText}
                onChange={(event) => setDeactivateText(event.target.value)}
                placeholder="DEACTIVATE"
                disabled={isDeactivatingLink}
                className="placeholder:text-zinc-400"
              />
            </InputContainer>
          </div>
          <Alert
            type="warning"
            title={
              "Your customers will no longer be able to access your pricing if you choose to deactivate this link"
            }
            message="Re-activating a link will generate a new one."
          />
          <DialogCTA>
            <Button
              color="red"
              type="submit"
              disabled={isDeactivatingLink || !isValidDeactivateString}
              loading={isDeactivatingLink}
            >
              DEACTIVATE
            </Button>
          </DialogCTA>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const IncludePricingToggle = ({
  storefrontLink,
}: {
  storefrontLink: StorefrontLinkListResponseType[number];
}) => {
  const [includePricing, setIncludePricing] = useState(
    storefrontLink.includePricing,
  );
  const storefrontLinkUpdateMutation = useStorefrontLinkUpdate(
    storefrontLink.id,
  );

  const handleToggleIncludePricing = () => {
    const newIncludePricing = !includePricing;
    storefrontLinkUpdateMutation.mutate(
      {
        includePricing: newIncludePricing,
      },
      {
        onSuccess: () => {
          setIncludePricing(newIncludePricing);
        },
      },
    );
  };

  return (
    <div className="flex items-center gap-2">
      <span>Include pricing</span>
      <Switch checked={includePricing} onChange={handleToggleIncludePricing} />
    </div>
  );
};

type ProductAndFormatTabMode =
  | "default"
  | "main-link"
  | "customer-link"
  | "price-tier-link";

type ProductAndFormatTabViewOption = Pick<
  ProductAndFormatTabViewActionButtonProps,
  "title" | "description" | "icon"
> & {
  mode: ProductAndFormatTabMode;
};

const mainTradeLinkOption: ProductAndFormatTabViewOption = {
  title: "General trade link",
  description: "Choose this link to share with a broad audience.",
  icon: "LINK",
  mode: "main-link",
};

const priceTierLinkOption: ProductAndFormatTabViewOption = {
  title: "Pricing link",
  description:
    "Choose a pricing link to share specific pricing with a group of customers or prospects.",
  icon: "TAG_MULTIPLE_OUTLINE",
  mode: "price-tier-link",
};

const customerLinkOption: ProductAndFormatTabViewOption = {
  title: "Customer link",
  description: "Choose a link unique to a specific customer.",
  icon: "PROFILE",
  mode: "customer-link",
};

const productAndFormatTabViewOptions: ProductAndFormatTabViewOption[] = [
  mainTradeLinkOption,
  priceTierLinkOption,
  customerLinkOption,
];

const StorefrontLinksProductAndFormatTab = () => {
  const [productAndFormatTabMode, setProductAndFormatTabMode] =
    useState<ProductAndFormatTabMode>("default");

  const isDefaultView = productAndFormatTabMode === "default";

  const content = (
    <>
      {productAndFormatTabMode === "main-link" && (
        <ProductAndFormatMainTradeLinkView />
      )}
      {productAndFormatTabMode === "price-tier-link" && (
        <ProductAndFormatPriceTierLinkView />
      )}
      {productAndFormatTabMode === "customer-link" && (
        <ProductAndFormatCustomerLinkView />
      )}
    </>
  );

  return (
    <div className="flex flex-col gap-8 py-6">
      <StorefrontLinksTabContainer
        title="Step 1 - choose link"
        description="Choose the type of link you wish to share before selecting product or format filters to apply"
      >
        <StorefrontLinkTypeSelector
          mode={productAndFormatTabMode}
          setMode={setProductAndFormatTabMode}
        />
      </StorefrontLinksTabContainer>
      {isDefaultView ? (
        <StorefrontLinksTabContainer
          title="Step 2 - choose product or format"
          description="Search for a product or apply filters by product format"
          disabled={isDefaultView}
        />
      ) : (
        content
      )}
    </div>
  );
};

const StorefrontLinkTypeSelector = ({
  mode,
  setMode,
}: {
  mode: ProductAndFormatTabMode;
  setMode: (mode: ProductAndFormatTabMode) => void;
}) => {
  if (mode === "default") {
    return (
      <div className="flex flex-col gap-2">
        {productAndFormatTabViewOptions.map((option) => (
          <ProductAndFormatTabViewActionButton
            key={option.mode}
            title={option.title}
            description={option.description}
            icon={option.icon}
            onClick={() => setMode(option.mode)}
          />
        ))}
      </div>
    );
  }

  const selectedOption = productAndFormatTabViewOptions.find(
    (option) => option.mode === mode,
  )!;

  return (
    <ProductAndFormatTabViewActionButton
      onClick={() => setMode("default")}
      icon="CHECK"
      selected
      title={selectedOption.title}
      description={selectedOption.description}
      rightComponent={
        <span className="flex gap-1">
          <span>Switch</span>
          <span>
            <Icon icon="SWAP_VERTICAL" size={0.8} />
          </span>
        </span>
      }
    />
  );
};

type ProductAndFormatTabViewActionButtonProps = {
  title: string;
  icon: IconType;
  description: string;
  rightComponent?: ReactNode;
  selected?: boolean;
  onClick: () => void;
};

const ProductAndFormatTabViewActionButton = ({
  title,
  icon,
  description,
  rightComponent,
  selected,
  onClick,
}: ProductAndFormatTabViewActionButtonProps) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-1 cursor-pointer items-center justify-between rounded-lg bg-white p-4 text-start text-sm text-zinc-800 hover:bg-zinc-50"
    >
      <span className="flex items-center gap-4">
        <ListItemIconTile
          icon={icon}
          size={1}
          className={selected ? "border-2 border-zinc-800" : ""}
        />
        <div className="flex flex-col">
          <span className="text-base font-semibold">{title}</span>
          <span className="text-zinc-700">{description}</span>
        </div>
      </span>
      {rightComponent}
    </button>
  );
};

const ProductAndFormatCustomerLinkView = () => {
  const storefrontLinkCreateMutation = useStorefrontLinkCreate();
  const {
    selectedFormats,
    supplierCurrency,
    supplierId,
    hasSelectedProduct,
    allFormats,
    isFetchingFormats,
    selectedProductId,
    supplierUrlId,
    products,
    setProductSearchTerm,
    setSelectedFormats,
    setSelectedProductId,
  } = useStorefrontLinkProductPreviewParameters();
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [debouncedCustomerSearchTerm] = useDebounceValue(
    customerSearchTerm,
    500,
  );
  const { selectedConnectionId, customer, setCustomer } =
    useStorefrontLinksV2Store();

  const supplierRelationshipListNewQuery = useSupplierRelationshipListNew(
    {
      query: [
        {
          type: "name",
          modifier: "INCLUDES",
          value: debouncedCustomerSearchTerm,
        },
      ],
      limit: 5,
    },
    {
      enabled: Boolean(debouncedCustomerSearchTerm),
    },
  );

  const handleActivateCustomerStorefrontLink = ({
    connectionId,
  }: {
    connectionId: number;
  }) => {
    storefrontLinkCreateMutation.mutate({
      type: "connection",
      SupplierRetailerConnectionId: connectionId,
      PriceTierId: null,
    });
  };

  const isCreatingCustomerStorefrontLink =
    storefrontLinkCreateMutation.isPending &&
    storefrontLinkCreateMutation.variables.type === "connection";

  const connections = supplierRelationshipListNewQuery.data?.flat() ?? [];

  const searchedCustomers: Customer[] = connections.map(
    convertConnectionToCustomer,
  );
  const customers: Customer[] = customer ? [customer] : searchedCustomers;
  const selectedConnection = connections.find(
    (connection) => connection.id === selectedConnectionId,
  );

  const hasSelectedCustomer = !!selectedConnectionId;

  return (
    <div className="flex flex-col gap-4">
      <StorefrontLinksTabCustomerSelector
        customers={customers}
        selectedCustomerId={selectedConnectionId}
        onSearchForCustomer={setCustomerSearchTerm}
        onSelectCustomer={(customerId) => {
          const selected =
            customers.find((c) => c.connectionId === customerId) ?? null;
          setCustomer(selected);
        }}
      />

      <StorefrontLinksTabContainer
        title="Step 2 - choose product or format"
        description="Search for a product or apply filters by product format"
        disabled={!hasSelectedCustomer}
      >
        {hasSelectedCustomer && (
          <>
            <StorefrontLinkItemForConnection
              connectionId={selectedConnectionId}
              supplierUrlId={supplierUrlId}
              activateProps={{
                isActivating: isCreatingCustomerStorefrontLink,
                onActivate: () =>
                  handleActivateCustomerStorefrontLink({
                    connectionId: selectedConnectionId,
                  }),
              }}
              formats={hasSelectedProduct ? selectedFormats : []}
              productId={selectedProductId}
              disabled={!hasSelectedProduct}
            />
            <div className="flex flex-col gap-4 md:flex-row">
              <div className="flex flex-col gap-4 rounded-lg bg-white p-4 md:min-w-1/3">
                <div className="w-full">
                  <StorefrontLinksProductSelector
                    onSearchForProduct={setProductSearchTerm}
                    selectedProductId={selectedProductId}
                    products={products}
                    onSelectProduct={setSelectedProductId}
                  />
                </div>
                <StorefrontLinksProductFormatPicker
                  loading={isFetchingFormats || !hasSelectedProduct}
                  formats={allFormats}
                  selectedFormats={selectedFormats}
                  onUpdateFormat={setSelectedFormats}
                />
              </div>
              <StorefrontLinksPreviewProduct
                productId={selectedProductId}
                priceTierId={selectedConnection?.PriceTier?.id ?? null}
                availabilityTierId={
                  selectedConnection?.AvailabilityTier?.id ?? null
                }
                connectionId={selectedConnectionId}
                supplier={{
                  id: supplierId,
                  currency: supplierCurrency,
                }}
              />
            </div>
          </>
        )}
      </StorefrontLinksTabContainer>
    </div>
  );
};

const ProductAndFormatPriceTierLinkView = () => {
  const [selectedPriceTierId, setSelectedPriceTierId] = useState<number | null>(
    null,
  );
  const {
    selectedFormats,
    supplierCurrency,
    supplierId,
    hasSelectedProduct,
    allFormats,
    isFetchingFormats,
    selectedProductId,
    supplierUrlId,
    products,
    setProductSearchTerm,
    setSelectedFormats,
    setSelectedProductId,
  } = useStorefrontLinkProductPreviewParameters();
  const { isPending: isActivating, handleActivateStorefrontPriceTierLink } =
    useActivateStorefrontLink();

  const [mode, setMode] = useState<"idle" | "selection">("idle");
  const priceTierListQuery = usePriceListList({
    query: [
      {
        type: "derivationTypeIncludes",
        value: ["custom", "flat", "percentage"],
        modifier: "IN",
      },
    ],
  });

  const priceTiers = priceTierListQuery.data?.pages.flat() ?? [];
  const hasSelectedPriceTier = !!selectedPriceTierId && mode === "selection";

  const selectedPriceTier = priceTiers.find(
    (priceTier) => priceTier.id === selectedPriceTierId,
  );

  const canDisplayProductAndFormat =
    hasSelectedPriceTier && !!selectedPriceTier;

  return (
    <div className="flex flex-col gap-4">
      {!hasSelectedPriceTier && (
        <StorefrontLinksTabContainer
          title="Pricing"
          description="Use these links when you want to share specific pricing with a group of customers or prospects"
        >
          <div className="flex max-h-64 flex-col">
            <div className="flex flex-col gap-2 overflow-y-auto">
              {priceTiers.map((priceTier) => (
                <ProductAndFormatLinkItemForPriceTier
                  key={priceTier.id}
                  priceTier={priceTier}
                  checked={selectedPriceTierId === priceTier.id}
                  onSelect={() => {
                    setSelectedPriceTierId(priceTier.id);
                    setMode("selection");
                  }}
                  onClose={() => {
                    setSelectedPriceTierId(null);
                    setMode("idle");
                  }}
                />
              ))}
            </div>
          </div>
        </StorefrontLinksTabContainer>
      )}
      <div className="flex flex-col gap-4">
        {canDisplayProductAndFormat && (
          <ProductAndFormatLinkItemForPriceTier
            priceTier={selectedPriceTier}
            checked
            onSelect={() => {}}
            onClose={() => {
              setSelectedPriceTierId(null);
              setMode("idle");
            }}
          />
        )}
        <StorefrontLinksTabContainer
          title="Step 2 - choose product or format"
          description="Search for a product or apply filters by product format"
          disabled={!canDisplayProductAndFormat}
        >
          {canDisplayProductAndFormat && (
            <>
              <StorefrontLinkItemForPriceTier
                priceTier={selectedPriceTier}
                supplierUrlId={supplierUrlId}
                activateProps={{
                  isActivating,
                  onActivate: () =>
                    handleActivateStorefrontPriceTierLink({
                      priceTierId: selectedPriceTierId,
                    }),
                }}
                formats={hasSelectedProduct ? selectedFormats : []}
                disabled={!hasSelectedProduct}
                productId={selectedProductId}
              />
              <div className="flex flex-col gap-4 md:flex-row">
                <div className="flex flex-col gap-4 rounded-lg bg-white p-4 md:min-w-1/3">
                  <div className="w-full">
                    <StorefrontLinksProductSelector
                      onSearchForProduct={setProductSearchTerm}
                      selectedProductId={selectedProductId}
                      products={products}
                      onSelectProduct={setSelectedProductId}
                    />
                  </div>
                  <StorefrontLinksProductFormatPicker
                    loading={isFetchingFormats || !hasSelectedProduct}
                    formats={allFormats}
                    selectedFormats={selectedFormats}
                    onUpdateFormat={setSelectedFormats}
                  />
                </div>
                <StorefrontLinksPreviewProduct
                  productId={selectedProductId}
                  priceTierId={selectedPriceTierId}
                  availabilityTierId={null}
                  connectionId={null}
                  supplier={{
                    id: supplierId,
                    currency: supplierCurrency,
                  }}
                />
              </div>
            </>
          )}
        </StorefrontLinksTabContainer>
      </div>
    </div>
  );
};

type ProductAndFormatLinkItemForPriceTierProps = {
  priceTier: {
    name: string;
    id: number;
  };
  checked: boolean;
  onSelect: () => void;
  onClose: () => void;
};
const ProductAndFormatLinkItemForPriceTier = ({
  checked,
  priceTier,
  onSelect,
  onClose,
}: ProductAndFormatLinkItemForPriceTierProps) => {
  const { handleActivateStorefrontPriceTierLink, isPending } =
    useActivateStorefrontLink();
  const storefrontLinkListQuery = useStorefrontLinkList(
    {
      query: [
        {
          type: "PriceTierId",
          modifier: "EQUALS",
          value: priceTier.id,
        },
      ],
    },
    {
      enabled: Boolean(priceTier.id),
    },
  );

  const priceTierStorefrontLink = storefrontLinkListQuery.data?.pages.flat()[0];

  return (
    <ProductAndFormatLinkItem
      isActivating={isPending}
      storefrontLinkId={priceTierStorefrontLink?.id ?? null}
      onActivate={() => {
        handleActivateStorefrontPriceTierLink({
          priceTierId: priceTier.id,
        });
      }}
      title={priceTier.name}
      checked={checked}
      rightComponent={
        checked && (
          <button
            type="button"
            onClick={onClose}
            className="px-2 hover:cursor-pointer"
          >
            <Icon icon="CLOSE" />
          </button>
        )
      }
      onSelect={onSelect}
    />
  );
};

type ProductAndFormatLinkItemProps = {
  isActivating: boolean;
  storefrontLinkId: number | null;
  title: string;
  checked?: boolean;
  rightComponent?: ReactNode;
  onSelect?: () => void;
  onActivate: () => void;
};
const ProductAndFormatLinkItem = ({
  storefrontLinkId,
  title,
  isActivating,
  checked,
  rightComponent,
  onSelect,
  onActivate,
}: ProductAndFormatLinkItemProps) => {
  const hasDefaultStorefrontLink = !!storefrontLinkId;
  return (
    <div className="flex cursor-pointer items-center justify-between gap-4 rounded-lg bg-white px-2 hover:bg-zinc-50">
      <button
        type="button"
        onClick={onSelect}
        className="flex flex-1 items-center gap-2 px-2 py-4 hover:cursor-pointer"
      >
        {hasDefaultStorefrontLink && (
          <input checked={checked} readOnly type="radio" />
        )}
        <span className="font-semibold">{title}</span>
      </button>
      {!hasDefaultStorefrontLink && (
        <SmallButton
          disabled={isActivating}
          loading={isActivating}
          color="slim"
          onClick={onActivate}
        >
          <Icon icon="LIGHTNING_BOLT" />
          <span>Activate link</span>
        </SmallButton>
      )}
      {Boolean(rightComponent) && rightComponent}
    </div>
  );
};

const ProductAndFormatMainTradeLinkView = () => {
  const storefrontLinkListQuery = useStorefrontLinkList({});
  const {
    supplierUrlId,
    allFormats,
    selectedFormats,
    selectedProductId,
    supplierCurrency,
    supplierId,
    products,
    isFetchingFormats,
    setProductSearchTerm,
    setSelectedFormats,
    setSelectedProductId,
  } = useStorefrontLinkProductPreviewParameters();
  const {
    isPending: isActivatingLink,
    mutationVariables,
    handleActivateStorefrontDefaultLink,
  } = useActivateStorefrontLink();
  const storefrontLinks = storefrontLinkListQuery.data?.pages.flat() ?? [];

  const defaultStorefrontLink = storefrontLinks.find(
    (storefrontLink) =>
      !storefrontLink.PriceTier?.id &&
      !storefrontLink.SupplierRetailerConnectionId,
  );

  const hasSelectedStorefrontLink = !!defaultStorefrontLink;

  const isActivating =
    isActivatingLink && mutationVariables?.type === "default";

  const canBuildStorefrontLink =
    defaultStorefrontLink?.password && supplierUrlId;

  const defaultStorefrontLinkAsString = canBuildStorefrontLink
    ? buildStorefrontLink({
        password: defaultStorefrontLink.password,
        supplierUrlId,
        formats: selectedFormats,
        productId: selectedProductId,
      })
    : "";

  return (
    <div className="flex flex-col gap-4">
      <ProductAndFormatLinkItem
        title="Main link"
        storefrontLinkId={defaultStorefrontLink?.id ?? null}
        isActivating={isActivating}
        onActivate={handleActivateStorefrontDefaultLink}
        checked
      />

      {hasSelectedStorefrontLink && (
        <StorefrontLinksTabContainer
          title="Step 2 - choose product or format"
          description="Search for a product or apply filters by product format"
        >
          <StorefrontLinkItem
            supplierUrlId={supplierUrlId}
            activateProps={{
              isActivating,
              onActivate: handleActivateStorefrontDefaultLink,
            }}
            storefrontLink={{
              id: defaultStorefrontLink.id,
              name: "Main link",
              link: defaultStorefrontLinkAsString,
            }}
            // Main links do not depend on products and are always available.
            disabled={false}
          />
          <div className="flex flex-col gap-4 md:flex-row">
            <div className="flex flex-col gap-4 rounded-lg bg-white p-4 md:min-w-1/3">
              <div className="w-full">
                <StorefrontLinksProductSelector
                  onSearchForProduct={setProductSearchTerm}
                  selectedProductId={selectedProductId}
                  products={products}
                  onSelectProduct={setSelectedProductId}
                />
              </div>
              <StorefrontLinksProductFormatPicker
                // For main links, we want to always show the product format picker unless formats are being fetched.
                loading={isFetchingFormats}
                formats={allFormats}
                selectedFormats={selectedFormats}
                onUpdateFormat={setSelectedFormats}
              />
            </div>
            <StorefrontLinksPreviewProduct
              productId={selectedProductId}
              priceTierId={null}
              availabilityTierId={null}
              connectionId={null}
              supplier={{
                id: supplierId,
                currency: supplierCurrency,
              }}
            />
          </div>
        </StorefrontLinksTabContainer>
      )}
    </div>
  );
};

const useActivateStorefrontLink = () => {
  const storefrontLinkCreateMutation = useStorefrontLinkCreate();

  const handleActivateStorefrontDefaultLink = () => {
    storefrontLinkCreateMutation.mutate({
      type: "default",
      SupplierRetailerConnectionId: null,
      PriceTierId: null,
    });
  };

  const handleActivateStorefrontPriceTierLink = ({
    priceTierId,
  }: {
    priceTierId: number;
  }) => {
    storefrontLinkCreateMutation.mutate({
      type: "priceTier",
      SupplierRetailerConnectionId: null,
      PriceTierId: priceTierId,
    });
  };

  const handleActivateCustomerStorefrontLink = ({
    connectionId,
  }: {
    connectionId: number;
  }) => {
    storefrontLinkCreateMutation.mutate({
      type: "connection",
      SupplierRetailerConnectionId: connectionId,
      PriceTierId: null,
    });
  };

  return {
    handleActivateStorefrontDefaultLink,
    handleActivateStorefrontPriceTierLink,
    handleActivateCustomerStorefrontLink,
    isPending: storefrontLinkCreateMutation.isPending,
    mutationVariables: storefrontLinkCreateMutation.variables,
  };
};

const useStorefrontLinkProductPreviewParameters = () => {
  const { business } = useSellarCurrentBusiness();
  const supplier = business?.Supplier;
  const supplierUrlId = supplier?.urlId ?? "";
  const supplierId = supplier?.id ?? 0;
  const supplierCurrency = business?.Supplier?.currency ?? "";
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [debouncedProductSearchTerm] = useDebounceValue(productSearchTerm, 500);
  const [selectedFormats, setSelectedFormats] = useState<string[]>([]);

  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null,
  );

  const productListQuery = useProductList(
    {
      limit: 5,
      sortBy: "name",
      sortDir: "asc",
      search: debouncedProductSearchTerm,
      query: [
        {
          type: "ParentId",
          modifier: "IS_NULL",
        },
        {
          type: "Products",
          modifier: "NOT_NULL",
        },
        {
          type: "type",
          modifier: "EQUALS",
          value: "product",
        },
        {
          type: "active",
          modifier: "EQUALS",
          value: true,
        },
        {
          type: "showAllProducts",
          modifier: "EQUALS",
          value: true,
        },
      ],
    },
    {
      enabled: Boolean(debouncedProductSearchTerm),
    },
  );

  const containerTypesQuery = useSupplierContainerTypesList({ supplierId });

  const productItems = productListQuery.data?.pages.flat() ?? [];
  const formats = containerTypesQuery.data?.pages.flat() ?? [];

  const products: Product[] = productItems.map(convertProductItemToProduct);

  const hasSelectedProduct = !!selectedProductId;

  const handleSelectProductId = (productId: number | null) => {
    if (!productId) {
      // Reset selected formats when the product is deselected
      setSelectedFormats([]);
    }
    setSelectedProductId(productId);
  };

  return {
    supplierUrlId,
    allFormats: formats,
    hasSelectedProduct,
    selectedFormats,
    products,
    selectedProductId,
    supplierId,
    supplierCurrency,
    isFetchingFormats: containerTypesQuery.isPending,
    setSelectedFormats,
    setProductSearchTerm,
    setSelectedProductId: handleSelectProductId,
  };
};

type Product = {
  id: number;
  imageUrl: string;
  kind: string;
  abv: number | null;
  name: string;
};

type StorefrontLinksProductSelectorProps = {
  selectedProductId: number | null;
  products: Product[];
  onSearchForProduct: (searchTerm: string) => void;
  onSelectProduct: (productId: number | null) => void;
};

const StorefrontLinksProductSelector = ({
  selectedProductId,
  products,
  onSearchForProduct,
  onSelectProduct,
}: StorefrontLinksProductSelectorProps) => {
  const [searchTerm, setSearchTerm] = useState("");

  const selectedProduct = products.find(
    (product) => product.id === selectedProductId,
  );

  if (selectedProduct && selectedProductId) {
    const productDescription = buildProductDescription({
      product: selectedProduct,
    });

    return (
      <div className="flex w-full items-center justify-between">
        <span className="flex items-center gap-4">
          <ProfilePicture logoUrl={selectedProduct.imageUrl} size="medium" />
          <div className="flex flex-col">
            <span className="font-semibold">{selectedProduct.name}</span>
            <span className="text-sm text-zinc-500">{productDescription}</span>
          </div>
        </span>
        <button
          type="button"
          className="cursor-pointer"
          onClick={() => onSelectProduct(null)}
        >
          <Icon icon="CLOSE" size={1} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-4">
      <InputContainer>
        <InputText
          value={searchTerm}
          onChange={(event) => {
            const value = event.target.value;
            setSearchTerm(value);
            onSearchForProduct(value);
          }}
          placeholder="Search for a product"
          className="placeholder:text-zinc-300"
        />
        <Icon icon="MAGNIFY" />
      </InputContainer>

      {products.length > 0 && (
        <div className="flex flex-col rounded-lg border border-zinc-200 bg-white">
          {products.map((product) => {
            const productDescription = buildProductDescription({ product });

            return (
              <button
                type="button"
                key={product.id}
                onClick={() => onSelectProduct(product.id)}
                className="flex cursor-pointer items-center justify-between border-b border-b-zinc-200 p-4 text-start first-of-type:rounded-t-lg last-of-type:rounded-b-lg last-of-type:border-b-0 hover:bg-zinc-100"
              >
                <span className="flex items-center gap-4">
                  <ProfilePicture logoUrl={product.imageUrl} size="medium" />
                  <div className="flex flex-col">
                    <span className="font-semibold">{product.name}</span>
                    <span className="text-sm text-zinc-500">
                      {productDescription}
                    </span>
                  </div>
                </span>
                <span>
                  <Icon icon="CHEVRON_RIGHT" size={1} />
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

type StorefrontLinksProductFormatPickerProps = {
  loading: boolean;
  formats: string[];
  selectedFormats: string[];
  onUpdateFormat: (formats: string[]) => void;
};

type Option = {
  label: string;
  value: string;
};

const StorefrontLinksProductFormatPicker = ({
  loading,
  formats,
  selectedFormats,
  onUpdateFormat,
}: StorefrontLinksProductFormatPickerProps) => {
  const formatOptions: Option[] = formats.map((format) => ({
    label: capitalize(format),
    value: format,
  }));

  if (loading) {
    return (
      <SkeletonContainer className="flex flex-1 flex-col gap-4 rounded-lg border border-zinc-200 p-4">
        <SkeletonText className="w-1/2" />
        <div className="grid grid-cols-3 gap-2">
          <SkeletonText className="h-6" />
          <SkeletonText className="h-6" />
          <SkeletonText className="h-6" />
          <SkeletonText className="h-6" />
          <SkeletonText className="h-6" />
        </div>
        <div className="flex flex-col gap-1">
          <SkeletonText />
          <SkeletonText />
        </div>
      </SkeletonContainer>
    );
  }

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-zinc-300 p-4">
      <span className="text-sm">Available formats:</span>
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => {
            onUpdateFormat([]);
          }}
          color="slim"
          size="small"
          selected={selectedFormats.length === 0}
        >
          <span>All formats</span>
          <input hidden type="checkbox" />
        </Button>
        {formatOptions.map((option) => {
          const isSelected = selectedFormats.includes(option.value);
          return (
            <Button
              onClick={() => {
                if (selectedFormats.includes(option.value)) {
                  onUpdateFormat(
                    selectedFormats.filter((format) => format !== option.value),
                  );
                } else {
                  onUpdateFormat(selectedFormats.concat(option.value));
                }
              }}
              selected={isSelected}
              color="slim"
              size="small"
              key={option.value}
            >
              <span>{option.label}</span>
              <input key={option.value} hidden type="checkbox" />
            </Button>
          );
        })}
      </div>
      <div className="flex gap-2">
        <div className="relative top-0.5">
          <Icon icon="INFORMATION_OUTLINE" size={0.6} />
        </div>
        <p className="text-sm">
          Selected formats filter your storefront for customers
        </p>
      </div>
    </div>
  );
};

type StorefrontLinksProductPreviewProps = {
  productId: number | null;
  priceTierId: number | null;
  availabilityTierId: number | null;
  supplier: {
    id: number;
    currency: string;
  };
  connectionId: number | null;
};

const StorefrontLinksPreviewProduct = ({
  productId,
  supplier,
  priceTierId,
  availabilityTierId,
  connectionId,
}: StorefrontLinksProductPreviewProps) => {
  const isDesktop = useMediaQuery("(min-width: 770px)");
  const parentProductQuery = useSupplierProduct(productId ?? 0, {
    enabled: Boolean(productId),
  });

  const productsListQuery = useStockList(
    {
      ParentId: productId,
      active: true,
      SupplierId: supplier.id,
      PriceTierId: priceTierId ?? 0,
      AvailabilityTierId: availabilityTierId ?? 0,
      SupplierPreviewSupplierRetailerConnectionId: connectionId ?? 0,
    },
    { enabled: Boolean(supplier.id) && Boolean(productId) },
  );

  const supplierStorefrontQuery = useSupplierStorefrontV2(
    supplier.id,
    {
      selectedPriceTierId: priceTierId ?? undefined,
      selectedAvailabilityTierId: availabilityTierId ?? undefined,
    },
    {
      enabled: Boolean(supplier.id),
    },
  );

  const isLoading =
    !supplier.id ||
    !productId ||
    parentProductQuery.isPending ||
    productsListQuery.isPending;

  if (isLoading) {
    return (
      <div className="flex min-h-64 flex-1 flex-col gap-4 rounded-lg bg-white p-4 md:flex-row">
        <div className="flex flex-1 flex-col gap-4">
          <SkeletonImage className="h-52" />
          <SkeletonImage className="relative left-4 -mt-15 h-20 w-20 rounded-full border border-white" />
          <SkeletonText className="w-1/2" />
          <SkeletonText className="w-1/2" />
          <div className="flex flex-col gap-2">
            <SkeletonText />
            <SkeletonText />
            <SkeletonText />
            <SkeletonText />
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-4">
          <div className="flex flex-col gap-4 rounded-lg border border-zinc-200">
            <div className="flex flex-col gap-4 px-4 py-2">
              <SkeletonText className="h-6 w-1/2" />
              <div className="flex gap-2">
                <SkeletonText className="h-6 w-6 rounded-full" />
                <SkeletonText className="h-6 w-6 rounded-full" />
                <SkeletonText className="h-6 w-6 rounded-full" />
                <SkeletonText className="h-6 w-1/3 rounded-full" />
              </div>
            </div>
            <div className="flex justify-between border-t border-t-zinc-200 px-4 py-2">
              <SkeletonText className="h-6 w-1/4" />
              <SkeletonText className="h-6 w-1/4" />
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-lg border border-zinc-200 p-4">
            <SkeletonText className="h-3 w-1/2" />
            <SkeletonText className="h-3 w-1/3" />
            <SkeletonText className="h-3 w-1/2" />
            <SkeletonText className="h-3" />
          </div>
          <div className="flex flex-col gap-2 rounded-lg border border-zinc-200 p-4">
            <SkeletonText className="h-3 w-1/2" />
            <SkeletonText className="h-3 w-1/3" />
            <SkeletonText className="h-3 w-1/2" />
            <SkeletonText className="h-3" />
          </div>
        </div>
      </div>
    );
  }

  const parent = parentProductQuery.data;
  const products = productsListQuery.data?.pages.flat() ?? [];

  const parentProductHeroImages = (
    parent?.ProductImages.map((productImage) => productImage.url) ?? []
  ).filter(Boolean);

  const hasProductHeroImages = parentProductHeroImages.length > 0;

  const parentProductImage = parent?.heroImageUrl || "";
  const parentProductName = parent?.name ?? "";
  const parentProductDescription = parent?.description ?? "";
  const parentProductImageUrl = parent?.imageUrl ?? "";
  const caseSizeRuleList =
    supplierStorefrontQuery.data?.CaseSizeRules.list ?? [];
  const priceTierDiscounts =
    supplierStorefrontQuery.data?.VolumeDiscounts?.PriceTierDiscounts ?? [];
  const hasPriceTierDiscounts = priceTierDiscounts.length > 0;

  return (
    <div className="flex w-full min-w-0 flex-col gap-4 rounded-lg bg-white p-4 md:flex-row">
      <div className="flex flex-col gap-4 md:w-1/2">
        {hasProductHeroImages && (
          <ImageSlider
            images={parentProductHeroImages}
            includeThumbnails={false}
            swipe={isDesktop}
          />
        )}
        {!hasProductHeroImages && (
          <CategoryImage
            imageUrl={parentProductImage}
            fallback="product-hero"
          />
        )}
        <div className="relative left-4 -mt-18">
          <ProfilePicture
            size="2xl"
            logoUrl={parentProductImageUrl}
            fallback="product-badge"
            name={parentProductName}
          />
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-2xl font-semibold text-zinc-800">
            {parentProductName}
          </span>
          <span className="text-zinc-700">
            {buildProductDescription({
              product: {
                abv: parent?.abv ?? null,
                kind: parent?.kind ?? "",
              },
            })}
          </span>
          <p>{parentProductDescription}</p>
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-4">
        <div className="flex max-h-96 flex-col">
          <div className="flex flex-col gap-4 overflow-y-auto">
            {products.map((product) => (
              <StorefrontLinksPreviewProductItem
                key={product.id}
                product={product}
                parent={parent}
              />
            ))}
          </div>
        </div>
        {hasPriceTierDiscounts && (
          <div className="flex flex-col gap-2 rounded-lg border border-zinc-200 p-4">
            <BadgePurple icon={"TAG_MULTIPLE_OUTLINE"}>
              Volume discounts
            </BadgePurple>
            <OrderDiscountSummary
              discounts={priceTierDiscounts}
              currency={supplier.currency}
              hideInfo={true}
              caseSizeRuleList={caseSizeRuleList}
            />
          </div>
        )}
      </div>
    </div>
  );
};

const StorefrontLinksPreviewProductItem = ({
  product,
  parent,
}: {
  product: StockListResponseType[number];
  parent: SupplierProductGetResponseType | undefined;
}) => {
  const productName = product.name;
  const dietaryList = buildDietaryList({
    childProduct: product,
    parentProduct: parent,
  });
  const isPreOrder = product.availableFrom
    ? dayjs().isBefore(product.availableFrom, "day")
    : false;

  const productPrice = product.ProductPriceTier?.price ?? 0;
  const productFullPrice = product.ProductPriceTier?.fullPrice ?? 0;

  const formattedProductPrice = formatCurrency(productPrice);

  const originalPrice = productFullPrice > productPrice ? productFullPrice : 0;

  const formattedOriginalPrice = formatCurrency(originalPrice);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-zinc-200">
      <div className="flex flex-col gap-2 p-4">
        <span className="text-2xl font-semibold">{productName}</span>
        <div className="flex flex-wrap gap-2">
          <DietaryDisplayList
            dietaryRequirements={{
              VG: dietaryList.includes("VG"),
              LF: dietaryList.includes("LF"),
              GF: dietaryList.includes("GF"),
            }}
            mode="badge"
          />
          <StockIndicator
            stock={product.stock}
            type="full"
            showStock
            canBackorder={product.canBackorder}
          />
          {isPreOrder && (
            <div className="flex items-center gap-2 rounded-full bg-[#EEF2FF] px-2 py-1 text-sm">
              <Icon icon="PROGRESS_CLOCK" color="#27272A" />
              {dayjs(product.availableFrom).format("DD/MM/YYYY")}
            </div>
          )}
        </div>
      </div>
      <div className="flex justify-between border-t border-t-zinc-200 p-4">
        <span className="font-semibold">Unit price (ex. VAT)</span>
        <div className="flex items-center gap-2 text-zinc-800">
          {Boolean(originalPrice) && (
            <span className="text-sm text-zinc-500 line-through">
              {formattedOriginalPrice}
            </span>
          )}
          <span className="text-xl font-semibold">{formattedProductPrice}</span>
        </div>
      </div>
    </div>
  );
};

const convertConnectionToCustomer = (
  connection: SupplierRelationshipListNewResponseType[number],
): Customer => {
  return {
    name: connection.Retailer.Business.name,
    postalCode: connection.Retailer.Business.DeliveryAddress.postalCode,
    connectionId: connection.id,
    logoUrl: connection.Retailer.Business.logoUrl ?? "",
    priceTierName: connection.PriceTier?.name ?? "",
    availabilityTierName: connection.AvailabilityTier?.name ?? "",
  };
};

const convertProductItemToProduct = (
  product: ProductListResponseType[number],
): Product => {
  return {
    id: product.id,
    imageUrl: product.imageUrl || product.Parent?.imageUrl || "",
    kind: product.kind ?? "",
    abv: product.abv,
    name: product.name ?? "",
  };
};

const buildProductDescription = ({
  product,
}: {
  product: Pick<Product, "abv" | "kind">;
}) => {
  const productAbv = product.abv ? `${product.abv}%` : "";
  const productKind = product.kind ? product.kind : "";
  return [productKind, productAbv].filter(Boolean).join(" // ");
};
